<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { REWARDS_HOME_ANCHOR } from "@apps/rewards/constants/constants";
    import { REWARDS_DISCOUNT_TYPES } from "@apps/rewards/constants/rewardsDiscountTypes";
    import { REWARDS_REWARD_TYPES } from "@apps/rewards/constants/rewardsRewardTypes";
    import RewardsPopUpWidgetLauncherManager from "@apps/rewards/widgets/rewards_popup_widget/utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";
    import { CartModule } from "@modules/cart/CartModule";
    import { CommonModule } from "@modules/common_module/CommonModule";
    import { ShopifyDiscountModule } from "@modules/discount/ShopifyDiscountModule";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import { MoneyModule } from "@modules/money/MoneyModule";
    import { lastValueFrom } from "rxjs";
    import { createEventDispatcher } from "svelte";
    import { container } from "tsyringe";

    import Button from "@components/Buttons/Button.svelte";
    import CopyToClipboard from "@components/CopyToClipboard.svelte";
    import Icon from "@components/Icon.svelte";
    import InfoIcon from "@components/icons/InfoIcon.svelte";
    import T from "@components/T.svelte";

    import { LEAVE_REVIEW_SOURCE } from "../../../constants";
    import RewardForReviewEntity from "../../../entities/RewardForReviewEntity";
    import rewardAnimation from "../../images/reward-animation.gif";

    export let reward: RewardForReviewEntity["reward"];
    export let source: LEAVE_REVIEW_SOURCE | undefined;

    const dispatch = createEventDispatcher();
    const { urlsBuilder, locationController, cartAvailabilityProvider } =
        container.resolve(CommonModule).exports;
    const moneyFormatter =
        container.resolve(MoneyModule).exports.moneyFormatter;
    const rewardsPopUpWidgetLauncherManager = container.resolve(
        RewardsPopUpWidgetLauncherManager
    );
    const shopifyDiscountApiService = container.resolve(ShopifyDiscountModule)
        .exports.shopifyDiscountApiService;
    const shopifyDiscountQuery = container.resolve(ShopifyDiscountModule)
        .exports.shopifyDiscountQuery;
    const cartService = container.resolve(CartModule).exports.cartService;
    const i18nModule = container.resolve(I18nModule);
    const isCartAvailable = cartAvailabilityProvider.isCartAvailable();

    const discountStatuses$ = shopifyDiscountQuery.discountStatus$;

    const applyDiscount = async (
        discountCode: string,
        rewardItemId: string
    ) => {
        await lastValueFrom(
            shopifyDiscountApiService.applyDiscount(discountCode, rewardItemId)
        );
    };

    const applyDiscountAndAddToCard = async (
        variantId: number,
        discountCode: string,
        rewardItemId: string
    ) => {
        await applyDiscount(discountCode, rewardItemId);
        await cartService.addToCart([
            {
                variantID: variantId,
                quantity: 1,
            },
        ]);

        locationController.goTo(urlsBuilder.createCartUrl());
    };

    const handleRedeem = () => {
        dispatch("closeReviewsFormModal");
        if (source === LEAVE_REVIEW_SOURCE.LEAVE_REVIEW_PAGE_WIDGET) {
            locationController.goTo(`/#${REWARDS_HOME_ANCHOR}`);
        } else {
            setTimeout(() => {
                rewardsPopUpWidgetLauncherManager.rewardsPopUpWidgetShow();
            }, 500);
        }
    };

    const handleApply = async () => {
        if (reward?.type === REWARDS_REWARD_TYPES.discount) {
            await applyDiscount(reward.discountCode, reward.discountCode);
        } else if (reward?.type === REWARDS_REWARD_TYPES.freeProduct) {
            await applyDiscountAndAddToCard(
                reward.variantId,
                reward.discountCode,
                reward.discountCode
            );
        }
    };

    const t_points_name$ = i18nModule.exports.i18n.selectT(
        "common.critical.Points",
        "points_name_plural"
    );

    $: discountStatuses = $discountStatuses$;
</script>

<div class="gw-rv-form-reward">
    <div class="gw-rv-form-reward__header">
        <h3 class="gw-rv-form-reward__title">
            <T
                namespace="reviews.ReviewsFormModalWidget"
                translationKey="reward_step_title"
            />
        </h3>
    </div>

    <div class="gw-rv-form-reward__box">
        <div class="gw-rv-form-reward__icon">
            <img
                class="gw-rv-form-reward__icon-img"
                src={rewardAnimation}
                alt=""
            />
        </div>

        {#if reward?.type === REWARDS_REWARD_TYPES.discount}
            <!-- Discount -->
            {#if reward.discountType === REWARDS_DISCOUNT_TYPES.fixedAmount}
                <p class="gw-rv-form-reward__text">
                    <T
                        namespace="reviews.ReviewsFormModalWidget"
                        translationKey="reward_step_reward_text_fixed_amount"
                        translationOptions={{
                            amount: reward.discountValue
                                ? moneyFormatter.format_with_currency(
                                      Math.abs(reward.discountValue)
                                  )
                                : "",
                        }}
                    />
                </p>
                <div class="gw-rv-form-reward__actions">
                    <CopyToClipboard
                        text={reward.discountCode}
                        buttonInsideInput
                    />
                    <Button
                        type="button"
                        class="gw-rv-form-reward__apply-btn"
                        variant="contained"
                        loading={discountStatuses[reward.discountCode] ===
                            STATUS.LOADING}
                        disabled={discountStatuses[reward.discountCode] ===
                            STATUS.READY}
                        on:click={handleApply}
                    >
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="reward_step_apply"
                        />
                    </Button>
                </div>
            {:else if reward?.discountType === REWARDS_DISCOUNT_TYPES.percentage}
                <p class="gw-rv-form-reward__text">
                    <T
                        namespace="reviews.ReviewsFormModalWidget"
                        translationKey="reward_step_reward_text_percentage"
                        translationOptions={{
                            amount: reward.discountValue || "",
                        }}
                    />
                </p>
                <div class="gw-rv-form-reward__actions">
                    <CopyToClipboard
                        text={reward.discountCode}
                        buttonInsideInput
                    />
                    <Button
                        type="button"
                        class="gw-rv-form-reward__apply-btn"
                        variant="contained"
                        loading={discountStatuses[reward.discountCode] ===
                            STATUS.LOADING}
                        disabled={discountStatuses[reward.discountCode] ===
                            STATUS.READY}
                        on:click={handleApply}
                    >
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="reward_step_apply"
                        />
                    </Button>
                </div>
            {:else if reward?.discountType === REWARDS_DISCOUNT_TYPES.freeShipping}
                <p class="gw-rv-form-reward__text">
                    <T
                        namespace="reviews.ReviewsFormModalWidget"
                        translationKey="reward_step_reward_text_free_shipping"
                        translationOptions={{
                            amount: reward.discountValue || "",
                        }}
                    />
                </p>
                <div class="gw-rv-form-reward__actions">
                    <CopyToClipboard
                        text={reward.discountCode}
                        buttonInsideInput
                    />
                    <Button
                        type="button"
                        class="gw-rv-form-reward__apply-btn"
                        variant="contained"
                        loading={discountStatuses[reward.discountCode] ===
                            STATUS.LOADING}
                        disabled={discountStatuses[reward.discountCode] ===
                            STATUS.READY}
                        on:click={handleApply}
                    >
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="reward_step_apply"
                        />
                    </Button>
                </div>
            {/if}

            <!-- Free Product -->
        {:else if reward?.type === REWARDS_REWARD_TYPES.freeProduct}
            <p class="gw-rv-form-reward__text">
                <T
                    namespace="reviews.ReviewsFormModalWidget"
                    translationKey="reward_step_reward_text_free_product"
                    translationOptions={{ product_title: reward.productTitle }}
                />
            </p>
            <div class="gw-rv-form-reward__actions">
                <CopyToClipboard text={reward.discountCode} buttonInsideInput />
                {#if isCartAvailable}
                    <Button
                        type="button"
                        class="gw-rv-form-reward__apply-btn"
                        variant="contained"
                        loading={discountStatuses[reward.discountCode] ===
                            STATUS.LOADING}
                        disabled={discountStatuses[reward.discountCode] ===
                            STATUS.READY}
                        on:click={handleApply}
                    >
                        <T
                            namespace="reviews.ReviewsFormModalWidget"
                            translationKey="reward_step_apply"
                        />
                    </Button>
                {/if}
            </div>

            <!-- Gift card -->
        {:else if reward?.type === REWARDS_REWARD_TYPES.giftCard}
            <p class="gw-rv-form-reward__text">
                <T
                    namespace="reviews.ReviewsFormModalWidget"
                    translationKey="reward_step_reward_text_gift_card"
                    translationOptions={{
                        amount: reward.initialValue
                            ? moneyFormatter.format_with_currency(
                                  Math.abs(reward.initialValue)
                              )
                            : "",
                    }}
                />
            </p>
            <div class="gw-rv-form-reward__actions">
                <CopyToClipboard text={reward.code} />
            </div>

            <!-- Points -->
        {:else if reward?.type === REWARDS_REWARD_TYPES.points}
            <p class="gw-rv-form-reward__text">
                <T
                    namespace="reviews.ReviewsFormModalWidget"
                    translationKey="reward_step_reward_text_points"
                    translationOptions={{
                        points: reward.points || "",
                        points_name: $t_points_name$,
                    }}
                />
            </p>
            <div class="gw-rv-form-reward__actions">
                <Button
                    type="button"
                    class="gw-rv-form-reward__redeem-btn"
                    on:click={handleRedeem}
                    variant="opacityOnHover"
                >
                    <T
                        namespace="reviews.ReviewsFormModalWidget"
                        translationKey="reward_step_redeem"
                    />
                </Button>
            </div>
        {/if}

        <div class="gw-rv-form-reward__help-info">
            <Icon width="14" height="14">
                <InfoIcon />
            </Icon>
            <span class="gw-rv-form-reward__help-text">
                <T
                    namespace="reviews.ReviewsFormModalWidget"
                    translationKey="reward_step_reward_help_info"
                />
            </span>
        </div>
    </div>
</div>

<style lang="postcss">
    .gw-rv-form-reward {
        max-width: 570px;

        @media screen and (max-width: 600px) {
            width: 100%;
        }

        &__title {
            font-size: 1.5em;
            line-height: 1.2;
            font-weight: 600;
            margin: 0;
            text-align: center;
        }

        &__box {
            box-sizing: border-box;
            margin-top: 2em;
            margin-right: auto;
            margin-left: auto;
            padding: 1.5em 1.25em 1.75em;
            border: 1px solid #e0e3ea;
            border-radius: 10px;
            max-width: 340px;
            width: 100%;
        }

        &__icon {
            margin: 0 auto;
            width: 95px;
            height: 95px;

            &-img {
                width: 100%;
                height: 100%;
            }
        }

        &__text {
            margin: 2px 0 0;
            font-size: 1em;
            line-height: 1.5;
            text-align: center;
        }

        &__actions {
            display: flex;
            gap: 8px;
            margin-top: 1.25em;
        }

        &__help-info {
            display: flex;
            align-items: center;
            gap: 0.25em;
            margin-top: 0.625em;
        }

        &__help-text {
            font-size: 0.75em;
            line-height: normal;
        }
    }

    :global(.gw-btn.gw-rv-form-reward__apply-btn) {
        max-width: 5em;
        width: 100%;
        border-radius: 10px;
    }

    :global(.gw-btn.gw-rv-form-reward__redeem-btn) {
        margin: 0 auto;
    }
</style>
